import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppSettingsService } from './app-settings.service';
import { IAuthData, IAuthDetails, LOCALSTORAGE_AUTHDETAILS } from './authentication.service';
import { hd } from './helpdesk.typings';
import { ClaimModel } from './user-info.service';
import {
  APP_USAGE_APP,
  APP_USAGE_APP_W,
  APP_USAGE_SHIFT_APP,
  APP_USAGE_SHIFT_APP_W,
  BATTERY_APP,
  BATTERY_APP_W,
  DATA_USAGE_APP,
  DATA_USAGE_APP_W,
  LAST_KNOWN_APP,
  LAST_KNOWN_APP_W,
  SIGNAL_STRENGTH_APP,
  SIGNAL_STRENGTH_APP_W,
  WEBAPP_USAGE_APP,
  WIFI_APP,
  WIFI_APP_W,
} from 'core/mappers/config-mapper/app-mapper.constants';
import { AllPlatforms, PlatformType } from '../../apps/did/src/app/modules/analytics-profiles/platform-constants';
import { IUserResourcePermission, ResourceType } from './roles/roles.models';

export interface IAuthorizationService {
  /** Checks if user is system admin */
  isSystemAdmin: () => boolean;

  /** Returns the user name for the current user */
  getUserName: () => string;

  /** Returns the user info ID for the current user */
  getUserId: () => string;

  /** Returns the current MC grant type */
  getMcGrantType: () => string;

  /** check if the user is an admin of any groups */
  isAnyGroupAdmin: () => boolean;

  /** Checks if the Group Id is the Group Admin */
  isGroupAdmin: (groupid: number) => boolean;

  /** check if the user is a technician of any groups */
  isAnyGroupTechnician: () => boolean;

  /** Checks if the Group Id is the Group Technician */
  isGroupTechnician: (groupid: number) => boolean;

  /** Checks if the Group Id is the Group Admin or Technician or current user is SysAdmin */
  isAdminOrTechnician: (groupId: number) => boolean;

  /** Checks if the current user is any Group Admin or Technician or SysAdmin */
  isAnyGroupAdminOrTechnician: () => boolean;

  /** Checks if the user has any role in any group */
  hasAnyRole: () => boolean;

  /** Gets the list of all the groups available for the user */
  getGroups: () => number[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService implements IAuthorizationService {
  /** Promise of load claims */
  claimPromise: Promise<hd.ISotiAssistClaims>;
  userProfilePermissions: IUserResourcePermission[] = [];

  constructor(
    @Inject('LOCALSTORAGE')
    private localStorage: Storage,
    private http: HttpClient,
    private appSettings: AppSettingsService
  ) {
    console.log('authorization service');
  }

  isSystemAdmin = (): boolean => {
    const claims = this.getClaims();
    return claims != null && claims.SystemAdmin === 'true';
  };

  hasCreateAccess = (): boolean => {
    if (this.isSystemAdmin()) {
      return true;
    }

    return this.isAllowed('AccessIM') && this.hasAnyCreateGroupAccess();
  };

  hasCreateGroupAccess = (groupId: number): boolean => {
    if (isNaN(groupId)) {
      return false;
    }

    if (this.isSystemAdmin()) {
      return true;
    }

    if (!this.isAllowed('AccessIM')) {
      return false;
    }

    const createIncidentsClaimArray = this.getCreateIncidentsGroupIdClaim();
    return createIncidentsClaimArray ? createIncidentsClaimArray.indexOf(groupId.toString()) > -1 : false;
  };

  hasAnyCreateGroupAccess = (): boolean => {
    if (this.isSystemAdmin()) {
      return true;
    }

    if (!this.isAllowed('AccessIM')) {
      return false;
    }

    const createIncidentsClaimArray = this.getCreateIncidentsGroupIdClaim();
    return createIncidentsClaimArray ? createIncidentsClaimArray.length > 0 : false;
  };

  isAllowed = (permission: hd.GeneralPermissionType): boolean => {
    if (this.isSystemAdmin()) {
      return true;
    }

    const permissions = this.getGeneralPermissions();
    return permissions != null && permissions.findIndex(p => p === permission) > -1;
  };

  allowedAppBaseline(profileId: number, osId: number) {
    const permId = this.userProfilePermissions.find(
      perm => perm.resourceId === profileId && perm.permissionId > 28
    ).permissionId;
    return PermissionAppList.find(permApp => permApp.permissionId === permId && (permApp.osId === osId || osId === AllPlatforms)).appBaseline;
  }

  isAllowedAny = (): boolean => {
    return (
      this.isAllowed('AccessIM') ||
      this.isAllowed('AccessOI') ||
      this.isAllowed('AccessLV') ||
      this.isAllowed('AccessUtil')
    );
  };

  /** See: IAuthorizationService.getUserName */
  getUserName = (): string => {
    const claims = this.getClaims();
    return claims ? claims.UserName : '';
  };

  /** See: IAuthorizationService.getUserId */
  getUserId = (): string => {
    const claims = this.getClaims();
    return claims ? claims.UserId : '';
  };

  /** See: IAuthorizationService.getMcGrantType */
  getMcGrantType = (): string => {
    const claims = this.getClaims();
    return claims ? claims.MCGrantType : null;
  };

  isAnyGroupAdmin = (): boolean => {
    const groupAdminArray = this.getGroupAdminClaim();
    return groupAdminArray ? groupAdminArray.length > 0 : false;
  };

  /** See: IAuthorizationService.isGroupAdmin */
  isGroupAdmin = (groupid: number): boolean => {
    if (groupid == null) {
      // check for null or undefined
      return false;
    }
    const groupAdminArray = this.getGroupAdminClaim();
    return groupAdminArray ? groupAdminArray.indexOf(groupid.toString()) > -1 : false;
  };

  /** See: IAuthorizationService.profileId */
  isProfileAdmin = (profileId: number): boolean => {
    if (profileId == null) {
      // check for null or undefined
      return false;
    }
    if (this.isSystemAdmin()) {
      return true;
    }
    const profileAdminArray = this.getProfileAdminClaim();
    return profileAdminArray ? profileAdminArray.indexOf(profileId.toString()) > -1 : false;
  };

  isWatchlistProfileAdmin(profileId: number) {
    const profileAdminArray = this.getWatchlistProfileAdminClaim();
    return profileId && (this.isSystemAdmin() || profileAdminArray?.indexOf(profileId.toString()) > -1);
  }

  /** See: IAuthorizationService.poolId */
  isPoolAdmin = (poolId: number): boolean => {
    if (poolId == null) {
      // check for null or undefined
      return false;
    }
    if (this.isSystemAdmin()) {
      return true;
    }
    const poolAdminArray = this.getPoolAdminClaim();
    return poolAdminArray ? poolAdminArray.indexOf(poolId.toString()) > -1 : false;
  };

  isAnyGroupTechnician = (): boolean => {
    const groupTechArray = this.getGroupTechnicianClaim();
    return groupTechArray ? groupTechArray.length > 0 : false;
  };

  /** See: IAuthorizationService.isGroupTechnician */
  isGroupTechnician = (groupid: number): boolean => {
    if (groupid == null) {
      // check for null or undefined
      return false;
    }
    const claims = this.getClaims();
    if (!claims || !claims.GroupTechnician) {
      return false;
    }
    const groupTechnicianArray = claims.GroupTechnician.split(',');
    return groupTechnicianArray.indexOf(groupid.toString()) > -1;
  };

  /** See: IAuthorizationService.isAdminOrTechnician */
  isAdminOrTechnician = (groupid: number): boolean => {
    return this.isSystemAdmin() || this.isGroupAdmin(groupid) || this.isGroupTechnician(groupid);
  };

  /** See: IAuthorizationService.isAdminOrTechnician */
  isAnyGroupAdminOrTechnician = (): boolean => {
    return this.isSystemAdmin() || this.isAnyGroupAdmin() || this.isAnyGroupTechnician();
  };

  hasAnyRole = (): boolean => {
    const groupIdsArray = this.getGroupIdClaim() || [];
    return this.isSystemAdmin() || groupIdsArray.length > 0;
  };

  hasAnyProfileAdminPermission = (): boolean => {
    const profileAdminArray = this.getProfileAdminClaim();
    return profileAdminArray && profileAdminArray.length > 0;
  };

  hasAnyPoolAdminPermission = (): boolean => {
    const poolAdminArray = this.getPoolAdminClaim();
    return poolAdminArray && poolAdminArray.length > 0;
  };

  getGroups = (): number[] => {
    const groupIdsArray = this.getGroupIdClaim() || [];
    return groupIdsArray.map(x => +x);
  };

  getCreateIncidentsGroups = (): number[] => {
    const groupIdsArray = this.getCreateIncidentsGroupIdClaim() || [];
    return groupIdsArray.map(x => +x);
  };

  getRoles = (): number[] => {
    const roleIdsArray = this.getRoleIdClaim() || [];
    return roleIdsArray.map(x => +x);
  };

  userHasProfilePermission(profileId): boolean {
    return (
      this.isSystemAdmin() ||
      this.userProfilePermissions.findIndex(
        permission =>
          (permission.resourceId === profileId &&
            permission.permissionId === ProfilePermissionEnum.ProfileFullAccess) ||
          permission.permissionId === ProfilePermissionEnum.ProfileViewAccess
      ) !== -1
    );
  }

  profileHasDashboardPermission(profileId): boolean {
    return (
      this.userProfilePermissions.findIndex(
        permission =>
          permission.resourceId === profileId &&
          permission.permissionId > 28 &&
          permission.permissionId in ProfilePermissionEnum
      ) !== -1
    );
  }

  userHasDashboardPermission(profileId: number, dashboardPermissionId: number[]): boolean {
    return (
      this.isSystemAdmin() ||
      this.userProfilePermissions.findIndex(
        permission => permission.resourceId === profileId && dashboardPermissionId.includes(permission.permissionId)
      ) !== -1
    );
  }

  /** get user authorization data from local storage */
  private getClaims = (): hd.ISotiAssistClaims => {
    const authDetails = this.getAuthDetails();
    return authDetails.claims;
  };

  public loadClaims = () => {
    this.claimPromise = new Promise<hd.ISotiAssistClaims>(async (resolve, reject) => {
      try {
        const authDetails = this.getAuthDetails();
        const claims = await this.http
          .get<ClaimModel[]>(this.appSettings.userInfoServiceUrl + 'current/claims')
          .toPromise();
        const assistClaims = claims.reduce((acc, claim) => {
          acc[claim.claimType] = claim.value;
          return acc;
        }, {} as hd.ISotiAssistClaims);

        authDetails.claims = assistClaims;
        this.setAuthData(authDetails);
        if (this.isAllowed('AccessOI')) {
          const userProfilePermissions = await this.http
            .get<IUserResourcePermission[]>(
              this.appSettings.accountServiceUrl + `current/resources/${ResourceType.Profile}/permissions`
            )
            .toPromise();

          this.setUserPermissions(userProfilePermissions);
        }

        resolve(authDetails.claims);
      } catch (error) {
        console.error('Error loading claims', error);
        reject(error);
      }
    });
    return this.claimPromise;
  };

  private setUserPermissions(userPermissions: IUserResourcePermission[]) {
    this.userProfilePermissions = userPermissions;
  }
  public isPartOfRole = (roleId: number): boolean => {
    const roleIds = this.getRoleIdClaim();
    if (roleIds == null || roleIds.length <= 0) {
      return false;
    }

    return roleIds.includes(roleId.toString());
  };

  private setAuthData = (authDetails: IAuthDetails) => {
    const authData: IAuthData = {
      claims: authDetails.claims,
    };

    this.localStorage.setItem(LOCALSTORAGE_AUTHDETAILS, JSON.stringify(authData));
  };

  getAuthDetails = (): IAuthDetails => {
    const authDetails: IAuthDetails = {
      isAuth: false,
      claims: null,
    };

    const rawAuthData = this.localStorage[LOCALSTORAGE_AUTHDETAILS];
    if (typeof rawAuthData === 'string') {
      const authData = <IAuthData>JSON.parse(rawAuthData);
      if (authData) {
        authDetails.isAuth = true;
        authDetails.claims = authData.claims;
      }
    }

    return authDetails;
  };

  private getGroupAdminClaim = (): string[] => {
    const claims = this.getClaims();
    if (!claims || !claims.GroupAdmin) {
      return null;
    }

    return claims.GroupAdmin.split(',');
  };

  private getProfileAdminClaim = (): string[] => {
    const claims = this.getClaims();
    if (!claims || !claims.ProfileAdmin) {
      return null;
    }

    return claims.ProfileAdmin.split(',');
  };

  private getWatchlistProfileAdminClaim = (): string[] => {
    const claims = this.getClaims();
    if (!claims || !claims.WatchlistProfileAdmin) {
      return null;
    }

    return claims.WatchlistProfileAdmin.split(',');
  };

  private getPoolAdminClaim = (): string[] => {
    const claims = this.getClaims();
    if (!claims || !claims.PoolAdmin) {
      return null;
    }

    return claims.PoolAdmin.split(',');
  };

  private getGeneralPermissions = (): string[] => {
    const claims = this.getClaims();
    if (!claims || !claims.GeneralPermission) {
      return null;
    }

    return claims.GeneralPermission.split(',');
  };

  private getGroupTechnicianClaim = (): string[] => {
    const claims = this.getClaims();
    if (!claims || !claims.GroupTechnician) {
      return null;
    }

    return claims.GroupTechnician.split(',');
  };

  private getGroupIdClaim = (): string[] => {
    /*  GroupId claim may be:
     *  * either a string (e.g. '1,2') or
     *  * an array of strings (e.g. ['1','1']).
     *  Both cases should be handled correctly. */

    const claims = this.getClaims();
    if (!claims || !claims.GroupId) {
      return null;
    }
    return this.getClaimValues(claims.GroupId);
  };

  private getCreateIncidentsGroupIdClaim = (): string[] => {
    /*  GroupId claim may be:
     *  * either a string (e.g. '1,2') or
     *  * an array of strings (e.g. ['1','1']).
     *  Both cases should be handled correctly. */

    const claims = this.getClaims();
    if (!claims || !claims.CreateIncidentsGroupId) {
      return null;
    }
    return this.getClaimValues(claims.CreateIncidentsGroupId);
  };

  private getRoleIdClaim = (): string[] => {
    /*  RoleId claim may be:
     *  * either a string (e.g. '1,2') or
     *  * an array of strings (e.g. ['1','1']).
     * Both cases should be handled correctly. */

    const claims = this.getClaims();
    if (!claims || !claims.RoleId) {
      return null;
    }
    return this.getClaimValues(claims.RoleId);
  };

  private getClaimValues = (claim): string[] => {
    if (!claim) {
      claim = '';
    }
    claim = '' + claim;

    return claim.split(',').filter(x => x !== '');
  };
}

export enum ProfilePermissionEnum {
  ProfileViewAccess = 27,
  ProfileFullAccess = 28,
  ProfileViewBatteryStatusDashboard = 33,
  ProfileViewAppUsageDashboard = 34,
  ProfileViewAppUsageShiftDashboard = 35,
  ProfileViewDataUsageDashboard = 36,
  ProfileViewLastLocationDashboard = 37,
  ProfileViewSignalStrengthDashboard = 38,
  ProfileViewWifiDashboard = 39,
  ProfileViewWebAppUsageDashboard = 43,
}

export interface PermissionApp {
  permissionId: ProfilePermissionEnum;
  appBaseline: string;
  osId: number;
}

export const PermissionAppList: PermissionApp[] = [
  {
    permissionId: ProfilePermissionEnum.ProfileViewBatteryStatusDashboard,
    appBaseline: BATTERY_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    appBaseline: APP_USAGE_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
    appBaseline: APP_USAGE_SHIFT_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewDataUsageDashboard,
    appBaseline: DATA_USAGE_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewLastLocationDashboard,
    appBaseline: LAST_KNOWN_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewSignalStrengthDashboard,
    appBaseline: SIGNAL_STRENGTH_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewWifiDashboard,
    appBaseline: WIFI_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewWebAppUsageDashboard,
    appBaseline: WEBAPP_USAGE_APP,
    osId: PlatformType.ANDROID,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewBatteryStatusDashboard,
    appBaseline: BATTERY_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewAppUsageDashboard,
    appBaseline: APP_USAGE_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewAppUsageShiftDashboard,
    appBaseline: APP_USAGE_SHIFT_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewDataUsageDashboard,
    appBaseline: DATA_USAGE_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewLastLocationDashboard,
    appBaseline: LAST_KNOWN_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewSignalStrengthDashboard,
    appBaseline: SIGNAL_STRENGTH_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewWifiDashboard,
    appBaseline: WIFI_APP_W,
    osId: PlatformType.WINDOWS,
  },
  {
    permissionId: ProfilePermissionEnum.ProfileViewWebAppUsageDashboard,
    appBaseline: WEBAPP_USAGE_APP,
    osId: PlatformType.WINDOWS,
  },
];
